import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, FileCheck, DollarSign, Building, BarChart2, Shield } from 'lucide-react';

const services = [
  {
    icon: Users,
    label: 'Union Payroll',
    title: 'Multi-Trade Union Contractor Payroll',
    subtitle: 'Union Workforce Management',
    desc: 'Comprehensive union payroll processing for multi-trade contractors. We handle proper classification, benefit fund remittances, and all certified reporting requirements for union workforce environments.',
    points: ['Multi-trade classification', 'Benefit fund remittances', 'Union dues processing', 'Collective bargaining compliance'],
  },
  {
    icon: FileCheck,
    label: 'Certified Payroll',
    title: 'Certified Payroll Reporting',
    subtitle: 'Federal Compliance Documentation',
    desc: 'Accurate, timely WH-347 certified payroll reports for federal and state-funded projects. Full compliance with Davis-Bacon Act requirements and proper documentation for every project.',
    points: ['WH-347 report preparation', 'Davis-Bacon Act compliance', 'Electronic submission support', 'Record retention management'],
  },
  {
    icon: DollarSign,
    label: 'Prevailing Wage',
    title: 'Prevailing Wage Compliance',
    subtitle: 'Wage Determination & Monitoring',
    desc: 'End-to-end prevailing wage determination, monitoring, and compliance documentation for public and state-funded construction projects across all applicable jurisdictions.',
    points: ['Wage determination analysis', 'Rate monitoring & updates', 'Compliance documentation', 'Multi-jurisdiction support'],
  },
  {
    icon: Building,
    label: 'Federal & State Projects',
    title: 'Federal & State Project Payroll',
    subtitle: 'Government Contract Compliance',
    desc: 'Specialized payroll management for federally-funded projects, DCAMM work, and state contract compliance. We know the specific requirements of public sector work.',
    points: ['Federally-funded project payroll', 'DCAMM work requirements', 'State contract compliance', 'Public sector documentation'],
  },
  {
    icon: BarChart2,
    label: 'Job Costing',
    title: 'Job-Cost Reporting & Allocation',
    subtitle: 'Project Financial Tracking',
    desc: 'Accurate payroll allocation by job and project for WIP tracking, job costing, and AIA billing support. Every dollar tracked to the right project, every time.',
    points: ['Per-project payroll allocation', 'WIP schedule integration', 'AIA billing support', 'Job-cost reporting'],
  },
  {
    icon: Shield,
    label: 'Workforce Compliance',
    title: 'Workforce & Employment Compliance',
    subtitle: 'Regulatory Compliance Management',
    desc: 'Employee classification reviews, I-9 compliance, state-specific workforce regulations, and comprehensive compliance documentation management to protect your business.',
    points: ['Employee classification review', 'I-9 verification management', 'State regulation compliance', 'Compliance documentation'],
  },
];

const complianceTable = [
  { area: 'Union Payroll', coverage: 'Multi-trade, multi-fund', projects: 'Private & Public Construction' },
  { area: 'Certified Payroll', coverage: 'WH-347 Reporting', projects: 'Federal & State Projects' },
  { area: 'Prevailing Wage', coverage: 'Davis-Bacon, State Wage Acts', projects: 'Public Works, DCAMM' },
  { area: 'Job Costing', coverage: 'Per-project allocation', projects: 'All Project Types' },
  { area: 'AIA Billing Support', coverage: 'G702/G703 Integration', projects: 'Commercial & Public' },
  { area: 'WIP Reporting', coverage: 'Work-in-progress schedules', projects: 'Bonding & Lender Use' },
  { area: 'I-9 & Classification', coverage: 'Employee status verification', projects: 'All Employers' },
  { area: 'State Compliance', coverage: 'Multi-state payroll laws', projects: 'Multi-state Operators' },
];

export default function PayrollCompliance() {
  const [activeTab, setActiveTab] = useState(0);
  const svc = services[activeTab];
  const SvcIcon = svc.icon;

  return (
    <div>
      {/* ── Hero ─────────────────────────────────── */}
      <section className="grid-bg pt-32 pb-14" data-testid="payroll-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-[#0ABFBC] text-[10px] font-bold uppercase tracking-[0.25em] mb-4">Payroll & Workforce Compliance</p>
            <h1 className="text-5xl sm:text-6xl font-bold text-[#1A2234] leading-[1.05] tracking-tight mb-5 max-w-3xl">
              Complex Payroll.<br />Handled with <span className="text-[#0ABFBC]">Precision.</span>
            </h1>
            <p className="text-lg text-[#475569] max-w-2xl leading-relaxed">
              Boston Federal specializes in payroll for complex, compliance-intensive environments — union contractors, certified payroll, prevailing wage, and federal/state funded projects.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── Services Tab Section ──────────────────── */}
      <section className="bg-white pb-0" data-testid="payroll-tabs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8 pt-12">
            <p className="text-[#0ABFBC] text-[10px] font-bold uppercase tracking-[0.25em] mb-2">Payroll Services</p>
            <h2 className="text-3xl font-bold text-[#1A2234]">Comprehensive Coverage</h2>
          </div>

          {/* Tab Bar */}
          <div className="border-b border-slate-100">
            <div className="flex overflow-x-auto no-scrollbar">
              {services.map((s, i) => (
                <button
                  key={s.label}
                  onClick={() => setActiveTab(i)}
                  data-testid={`payroll-tab-${i}`}
                  className={`px-5 py-4 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors duration-200 ${
                    i === activeTab ? 'text-[#0ABFBC] border-[#0ABFBC]' : 'text-[#475569] border-transparent hover:text-[#1A2234]'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.28 }}
              className="py-14 grid grid-cols-1 lg:grid-cols-5 gap-12 items-center"
            >
              <div className="lg:col-span-3">
                <div className="w-12 h-12 rounded-xl bg-[#0ABFBC]/10 flex items-center justify-center mb-5">
                  <SvcIcon size={22} strokeWidth={1.5} className="text-[#0ABFBC]" />
                </div>
                <p className="text-[#0ABFBC] text-[10px] font-bold uppercase tracking-[0.2em] mb-2">{svc.subtitle}</p>
                <h3 className="text-3xl font-bold text-[#1A2234] mb-4 leading-tight">{svc.title}</h3>
                <p className="text-[#475569] leading-relaxed mb-7">{svc.desc}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                  {svc.points.map(p => (
                    <div key={p} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#0ABFBC] flex-shrink-0 mt-[7px]" />
                      <span className="text-[#475569] text-sm">{p}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="lg:col-span-2 flex justify-center">
                <div className="w-52 h-52 rounded-full bg-[#0ABFBC]/5 flex items-center justify-center">
                  <div className="w-36 h-36 rounded-full bg-[#0ABFBC]/8 flex items-center justify-center">
                    <div className="w-20 h-20 rounded-full bg-[#0ABFBC]/12 flex items-center justify-center">
                      <SvcIcon size={40} strokeWidth={0.9} className="text-[#0ABFBC]/60" />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* ── Compliance Coverage Table ─────────────── */}
      <section className="bg-[#F8FAFC] py-20" data-testid="compliance-table">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <p className="text-[#0ABFBC] text-[10px] font-bold uppercase tracking-[0.25em] mb-3">Compliance Coverage</p>
            <h2 className="text-3xl font-bold text-[#1A2234] leading-tight">What We Cover</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="compliance-coverage-table">
              <thead>
                <tr className="border-b-2 border-[#0ABFBC]/20">
                  <th className="text-left py-4 px-6 text-[10px] font-bold uppercase tracking-[0.2em] text-[#1A2234]">Area</th>
                  <th className="text-left py-4 px-6 text-[10px] font-bold uppercase tracking-[0.2em] text-[#1A2234]">Coverage</th>
                  <th className="text-left py-4 px-6 text-[10px] font-bold uppercase tracking-[0.2em] text-[#1A2234]">Applicable Projects</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {complianceTable.map((row, i) => (
                  <motion.tr
                    key={row.area}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.04 }}
                    className="hover:bg-white transition-colors"
                  >
                    <td className="py-4 px-6 font-semibold text-[#1A2234] text-sm">{row.area}</td>
                    <td className="py-4 px-6 text-[#475569] text-sm">{row.coverage}</td>
                    <td className="py-4 px-6">
                      <span className="rounded-full bg-[#0ABFBC]/10 text-[#09A09D] px-3.5 py-1 text-xs font-bold border border-[#0ABFBC]/20">
                        {row.projects}
                      </span>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ── Dark CTA ──────────────────────────────── */}
      <section className="dark-grid-bg py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
            <h2 className="text-4xl font-bold text-white mb-4">Complex Payroll Needs a Specialist</h2>
            <p className="text-slate-400 text-lg mb-8 max-w-xl mx-auto">Let's discuss your payroll and compliance requirements.</p>
            <Link to="/contact" data-testid="payroll-cta" className="bg-[#0ABFBC] text-white px-8 py-3.5 rounded-md font-semibold hover:bg-[#09A09D] transition-colors inline-flex items-center gap-2">
              Talk to a Specialist <ArrowRight size={17} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
